import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import UserInstaPosts from "./UserInstaPosts";
import {
  AddFlex,
  CustomInput,
  CustomText,
  DialogHeader,
  Gap,
  SecondaryActionButton,
} from "../../reusableStyles";
import InstaCommentToDMAutomationKeywords from "./InstaCommentToDMAutomationKeywords";
import InstaCommentToDMAutomationOpeningMessage from "./InstaCommentToDMAutomationOpeningMessage";
import InstaCommentToDMAutomationMessage from "./InstaCommentToDMAutomationMessage";
import InstaCommentToDMOtherAutomations, {
  automationTypes,
} from "./InstaCommentToDMOtherAutomations";
import { Button, Input } from "antd";
import {
  capitalizeFirstLetter,
  generateUUID,
  isArrayNullOrUndefined,
  isNullOrUndefined,
  isStringNullOrUndefined,
  isValidURL,
  logAnalytics,
  logMetaPixelEvent,
} from "../../utils";
import { CircularProgress, Dialog } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { primaryActionColor } from "../../constants";
import InstagramDMPreview from "./InstagramDMPreview";
import useWindowSize from "../../Hooks/useWindowSize";
import ButtonWithAlertDialog from "../../CreateOffering/Components/ButtonWithAlertDialog";
import ContactListTable from "./ContactListTable";
import { Close } from "@mui/icons-material";
import { callNonStoreApi } from "../../NonStoreApis";
import {
  PauseOrResumeAutomationApi,
  RegisterCommentToDm,
} from "../../Api/ApiEndPoints";
import useDialogActionsHook from "../../CustomHooks.js/useDialogActionsHook";
import Tabs from "../../Tabs";
import ResponsiveContactsList from "./ResponsiveContactsList";
import AutomationTags from "./AutomationTags";
import useIntegrationHooks from "../../CustomHooks/useIntegrationHooks";
import useInstagramIntegrationHook from "../../CustomHooks/useInstagramIntegrationHook";

const handleCheckDmMessageValidation = (dmMessage) => {
  let errors = {};
  if (isStringNullOrUndefined(dmMessage?.message)) {
    errors["dmMessageMessage"] = "Add a DM Message";
  }
  if (
    !isStringNullOrUndefined(dmMessage?.link) ||
    !isStringNullOrUndefined(dmMessage?.buttonText)
  ) {
    if (
      (dmMessage?.link && !isValidURL(dmMessage?.link)) ||
      isStringNullOrUndefined(dmMessage?.link)
    ) {
      errors["dmMessageLink"] = "Enter a valid url";
    }
    if (isStringNullOrUndefined(dmMessage?.buttonText)) {
      errors["dmMessageButtonText"] = "Add button label to continue";
    }
  }
  return Object.values(errors).length > 0 ? errors : null;
};

const handleCheckCommentAutomationValidations = (
  automationData,
  stage,
  isCampagin
) => {
  let errors = {};
  if (
    !isCampagin &&
    (isNullOrUndefined(automationData.mediaData) ||
      (automationData.mediaData.type !== "all" &&
        automationData.mediaData.type !== "nextPost" &&
        isStringNullOrUndefined(automationData.mediaData.mediaId)))
  ) {
    errors["mediaData"] = "Select add a media";
  }
  if (stage === 1) return errors;
  // Start of keywords validations
  if (
    isNullOrUndefined(automationData.keywords) ||
    (automationData.keywords.type !== "any" &&
      isArrayNullOrUndefined(automationData.keywords.keywords))
  ) {
    errors["keywords"] = "Add the words that suit best for automation.";
  }

  if (stage === 2) return errors;

  // Start of dmMessage validations
  if (automationData.dmMessage.isAdvanced) {
    if (isNullOrUndefined(automationData?.dmMessage?.data)) {
      errors["dmMessageMessage"] = "Add a response to every keyword";
    } else {
      const responses = Object.keys(automationData.dmMessage.data).filter(
        (word) => word !== "default"
      );
      if (responses.length !== automationData.keywords.keywords.length) {
        errors["dmMessageMessage"] = "Add a response to every keyword";
      }
    }
  } else {
    if (automationData?.dmMessage.type === "products") {
      if (isArrayNullOrUndefined(automationData?.dmMessage?.products.data)) {
        errors["dmMessageMessage"] = "Please Add Atleast One Product";
      }
    } else {
      const dmMessageErrors = handleCheckDmMessageValidation(
        automationData?.dmMessage
      );
      if (dmMessageErrors) {
        errors = { ...errors, ...dmMessageErrors };
      }
    }
  }

  if (stage === 3) return errors;

  // Start of openingMessage validations
  if (isStringNullOrUndefined(automationData?.openingMessage?.message)) {
    errors["openingMessageMessage"] = "Add an intro message";
  }
  if (isStringNullOrUndefined(automationData?.openingMessage?.buttonText)) {
    errors["openingMessageButtonText"] = "Add button label to continue";
  }

  if (stage === 4) return errors;
  if (
    automationData.otherAutomations?.some(
      (automation) => automation.type === "reply-to-comments"
    )
  ) {
    const replyToComments = automationData.otherAutomations?.find(
      (automation) => automation.type === "reply-to-comments"
    );
    if (replyToComments.body.isAi) {
      if (isStringNullOrUndefined(replyToComments.body.context)) {
        errors["reply-to-comments-data"] =
          "Please provide a detailed context about the post/reel to the AI";
      }
    } else {
      let replies = replyToComments.body.filter((word) => word.length > 0);
      replies = [...new Set(replies)];
      if (replies.length !== 7) {
        errors["reply-to-comments-data"] =
          "Add 7 unique replies so they feel natural.";
      }
    }

    // Add validatoions for other remaining automations.
  } else {
    automationData.otherAutomations?.forEach((automation) => {
      if (isStringNullOrUndefined(automation.body)) {
        errors[automation.type] = "Enter this message";
      }
    });
  }

  return errors;
};

const getValidationForCurrentStep = (automationType) => {
  switch (automationType) {
    case automationTypes.REQUEST_MOBILE_NUMBER:
      return "^\\+\\d{10,15}$";
    case automationTypes.REQUEST_EMAIL:
      return "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$";
    default:
      return null;
  }
};

const initialAutomationData = (selectedAutomationData, isCampagin) => {
  return (
    selectedAutomationData || {
      id: generateUUID(),
      campagin_automation_id: isCampagin ? generateUUID() : null,
      mediaData: {
        type: "specific",
      },
      keywords: {
        type: "specific",
      },
      openingMessage: {
        message:
          "Hey there! I'm so happy you're here, thanks so much for your interest. Click below and I'll send you the link in just a sec",
        buttonText: "Send me the link",
      },
      dmMessage: {
        message: "",
        link: "",
        buttonText: "",
      },
    }
  );
};

function CreateUpdateInstaCommentAutomation({
  selectedAutomationData,
  setSelectedAutomationData,
  onClose,
  campaignOwnerUsername,
  isOwnerInstagramIntegrated,
  handleRefresh,
  isCampagin,
  campaginCallback,
}) {
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const { isMobileView, windowSize } = useWindowSize();
  const [staticAutomationData] = useState(
    initialAutomationData(selectedAutomationData, isCampagin)
  );
  const [automationData, setAutomationData] = useState(
    initialAutomationData(selectedAutomationData, isCampagin)
  );
  const [stage, setStage] = useState(selectedAutomationData ? 5 : 1);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const { closeDialog, openDialog, isDialogOpen } = useDialogActionsHook();

  const handleSetAutomationData = (key, value) => {
    setAutomationData((prev) => ({ ...prev, [key]: value }));
  };
  const [showContacts, setShowContacts] = useState(false);

  const { credentials } = useSelector((state) => state.instagram);

  const { isIntegrationActivated } = useIntegrationHooks(
    "instagram_connect",
    () => {},
    () => {}
  );

  const getEndFlow = (dmMessage) => {
    if (dmMessage.isAdvanced) {
      const endFlow = { isAdvanced: true, data: {} };
      Object.entries(dmMessage.data).forEach(([key, value]) => {
        if (value.type === "products") {
          endFlow.data[key] = value;
        } else {
          endFlow.data[key] = !isStringNullOrUndefined(value.link)
            ? {
                message: {
                  type: "buttons",
                  text: value.message,
                  buttons: [
                    {
                      type: "web_url",
                      title: value.buttonText,
                      url: value.link,
                    },
                  ],
                },
              }
            : {
                message: {
                  type: "text",
                  text: value.message,
                },
              };
        }
      });
      return endFlow;
    }
    if (dmMessage.type === "products") {
      return dmMessage;
    } else {
      return !isStringNullOrUndefined(dmMessage.link)
        ? {
            message: {
              type: "buttons",
              text: dmMessage.message,
              buttons: [
                {
                  type: "web_url",
                  title: dmMessage.buttonText,
                  url: dmMessage.link,
                },
              ],
            },
          }
        : {
            message: {
              type: "text",
              text: dmMessage.message,
            },
          };
    }
  };

  const getFlowObject = (automationData) => {
    const flow = {};
    flow.start = {
      message: {
        type: "buttons",
        text: automationData.openingMessage.message,
        buttons: [
          {
            type: "postback",
            title: automationData.openingMessage.buttonText,
            payload: `${
              automationData.mediaData.mediaId
            }_start_${generateUUID()}_${automationData.id}`,
            action: "end",
          },
        ],
      },
    };
    const otherAutomations = automationData.otherAutomations?.filter(
      (automation) => automation.type !== automationTypes.REPLY_TO_COMMENTS
    );

    if (!isArrayNullOrUndefined(otherAutomations)) {
      let i = 0;
      do {
        const nextStep = otherAutomations[i + 1]?.type || "end";
        const currentStep = otherAutomations[i].type;
        const validationOfCurrentStep =
          getValidationForCurrentStep(currentStep);
        if (i === 0) {
          flow.start.message.buttons[0].action = currentStep;
        }
        if (currentStep === automationTypes.REQUEST_TO_FOLLOW) {
          flow[automationTypes.REQUEST_TO_FOLLOW] = {
            condition: "followerCheck",
            // trueStep: "end",
            trueStep: nextStep,
            falseStep: "followPrompt",
          };
          flow.followPrompt = {
            message: {
              type: "buttons",
              text: otherAutomations[i].body,
              buttons: [
                {
                  type: "web_url",
                  title: "Go to my profile",
                  url: `https://instagram.com/${
                    campaignOwnerUsername || credentials?.username
                  }`,
                },
                {
                  type: "postback",
                  title: "I am following you",
                  payload: `${
                    automationData.mediaData.mediaId
                  }_${currentStep}_${generateUUID()}_${automationData.id}`,
                  action: automationTypes.REQUEST_TO_FOLLOW,
                },
              ],
            },
          };
        } else {
          flow[currentStep] = {
            message: {
              type: "text",
              text: otherAutomations[i].body,
              validation: validationOfCurrentStep,
              nextStep,
            },
          };
        }
        i = i + 1;
      } while (i < otherAutomations.length);
    }
    flow.end = getEndFlow(automationData.dmMessage);
    return flow;
  };

  const handleGetPayload = () => {
    const data = {},
      mediaData = automationData.mediaData;

    //A final check to make sure weather the media is updated or not.
    if (
      staticAutomationData.mediaData.mediaId ===
      automationData.mediaData.mediaId
    ) {
      mediaData.isMediaUpdated = false;
    } else {
      mediaData.isMediaUpdated = true;
    }
    const flowObject = getFlowObject(automationData); // building the flow object
    data.flow = flowObject;
    data.tags = automationData.tags;
    data.keywords = automationData.keywords;
    if (
      automationData.otherAutomations?.some(
        (automation) => automation.type === automationTypes.REPLY_TO_COMMENTS
      )
    ) {
      data.commentReplies = automationData.otherAutomations.find(
        (automation) => automation.type === automationTypes.REPLY_TO_COMMENTS
      ).body; // this is always the 1st object in the otherAutomations
    }
    return {
      data,
      mediaData,
      id: automationData.id,
      campagin_automation_id: automationData.campagin_automation_id,
      isEdit,
      name: automationData.name,
      instaUserId: credentials.user_id,
      instaId: credentials.id,
      status: automationData.status,
    };
  };

  const handleGoLive = async (forceUpdate) => {
    setIsApiLoading(true);
    const _errors = handleCheckCommentAutomationValidations(
      automationData,
      null,
      isCampagin
    );
    if (Object.keys(_errors).length > 0) {
      setErrors(_errors);
      setIsApiLoading(false);
      return;
    }
    try {
      // do live api
      const payload = handleGetPayload();
      if (isCampagin) {
        return campaginCallback("metaData", payload);
      }
      payload.forceUpdate = forceUpdate;
      const response = await callNonStoreApi(RegisterCommentToDm, payload);
      enqueueSnackbar("Automation Live", {
        variant: "success",
      });
      setAutomationData((prev) => ({
        ...prev,
        mediaData: {
          ...prev.mediaData,
          isMediaUpdated: false,
        },
      }));

      if (!isEdit) {
        await logAnalytics("automation_creation", {
          flow_id: automationData.id,
        });
        logMetaPixelEvent("AutomationCreated", true);
      }

      await handleRefresh();
      setIsEdit(false);
      onClose();
      return;
    } catch (error) {
      if (error.response.status === 403) {
        openDialog();
      } else {
        enqueueSnackbar("Something went wrong", {
          variant: "error",
        });
      }
    } finally {
      setIsApiLoading(false);
    }
  };

  const handlePauseOrResumeAutomation = async () => {
    try {
      if (isCampagin) {
        // make changes wrt
        if (isCampagin) {
          return campaginCallback("automationStatus", {
            campagin_automation_id: automationData.campagin_automation_id,
            status: automationData.status === "live" ? "paused" : "live",
          });
        }
      }
      setIsApiLoading(true);
      const response = await callNonStoreApi(PauseOrResumeAutomationApi, {
        id: automationData.id,
        status: automationData.status === "live" ? "paused" : "live",
      });
      if (response.data.message === "success") {
        setSelectedAutomationData((prev) => ({
          ...prev,
          status: response.data.status,
        }));
        setAutomationData((prev) => ({
          ...prev,
          status: response.data.status,
        }));
        await handleRefresh();
        enqueueSnackbar(
          `Automation ${
            response.data.status === "live" ? "is back live!" : "paused"
          }`,
          {
            variant: "success",
          }
        );
      } else {
        enqueueSnackbar("something went wrong", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("something went wrong", {
        variant: "error",
      });
    } finally {
      setIsApiLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const _errors = handleCheckCommentAutomationValidations(
        automationData,
        null,
        isCampagin
      );
      setErrors(_errors);
    }
  }, [automationData]);

  const getTopBarButtons = () => {
    if (selectedAutomationData && !isEdit) {
      if (
        selectedAutomationData &&
        selectedAutomationData.status === "paused"
      ) {
        return (
          <AddFlex gap="10px">
            <Button
              variant="solid"
              onClick={() => {
                if (!isIntegrationActivated()) {
                  enqueueSnackbar("Please integrate an instagram account", {
                    variant: "error",
                  });
                  return;
                }
                logMetaPixelEvent("AutomationUpdate", true);
                setIsEdit(true);
              }}
              size="medium"
            >
              {"Edit"}
            </Button>
            <ButtonWithAlertDialog
              component={
                <Button
                  variant="solid"
                  style={{
                    backgroundColor: primaryActionColor,
                    color: "white",
                  }}
                  size="medium"
                >
                  {isApiLoading ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    "Resume"
                  )}
                </Button>
              }
              buttons={[
                {
                  title: "Proceed Anyway",
                  action: async () => {
                    if (!isIntegrationActivated()) {
                      enqueueSnackbar("Please integrate an instagram account", {
                        variant: "error",
                      });
                      return;
                    }
                    logMetaPixelEvent("AutomationResumed", true);
                    handlePauseOrResumeAutomation();
                  },
                },
              ]}
              title={"Are You Sure?"}
            />
          </AddFlex>
        );
      }
      return (
        <AddFlex gap="10px">
          <Button
            variant="solid"
            onClick={() => {
              if (!isIntegrationActivated()) {
                enqueueSnackbar("Please integrate an instagram account", {
                  variant: "error",
                });
                return;
              }
              setIsEdit(true);
            }}
            size="medium"
          >
            {"Edit"}
          </Button>
          <ButtonWithAlertDialog
            component={
              <Button
                variant="solid"
                style={{
                  color: "red",
                }}
                size="medium"
              >
                {isApiLoading ? (
                  <CircularProgress size={14} sx={{ color: "red" }} />
                ) : (
                  "Stop"
                )}
              </Button>
            }
            buttons={[
              {
                title: "Proceed Anyway",
                action: async () => {
                  if (!isIntegrationActivated()) {
                    enqueueSnackbar("Please integrate an instagram account", {
                      variant: "error",
                    });
                    return;
                  }
                  logMetaPixelEvent("AutomationPaused", true);
                  handlePauseOrResumeAutomation();
                },
              },
            ]}
            title={"Are You Sure?"}
          />
          {isCampagin && (
            <ButtonWithAlertDialog
              component={
                <Button
                  variant="solid"
                  style={{
                    color: "red",
                  }}
                  size="medium"
                >
                  {isApiLoading ? (
                    <CircularProgress size={14} sx={{ color: "red" }} />
                  ) : (
                    "Delete"
                  )}
                </Button>
              }
              buttons={[
                {
                  title: "Proceed Anyway",
                  action: async () => {
                    if (!isIntegrationActivated()) {
                      enqueueSnackbar("Please integrate an instagram account", {
                        variant: "error",
                      });
                      return;
                    }
                    logMetaPixelEvent("AutomationDeleted", true);
                    campaginCallback("deleteAutomation", {
                      campagin_automation_id:
                        selectedAutomationData.campagin_automation_id,
                    });
                  },
                },
              ]}
              title={"Are You Sure?"}
            />
          )}
        </AddFlex>
      );
    }
    if (selectedAutomationData && isEdit) {
      return (
        <AddFlex gap="10px">
          <Button
            variant="solid"
            onClick={() => {
              setIsEdit(false);
            }}
            size="medium"
          >
            {"Cancel"}
          </Button>
          <Button
            variant="solid"
            // color={primaryActionColor}
            style={{
              backgroundColor: primaryActionColor,
              color: "white",
            }}
            onClick={() => {
              if (!isIntegrationActivated()) {
                enqueueSnackbar("Please integrate an instagram account", {
                  variant: "error",
                });
                return;
              }
              handleGoLive();
            }}
            size="medium"
          >
            {isApiLoading ? (
              <CircularProgress size={14} sx={{ color: "white" }} />
            ) : (
              "Update"
            )}
          </Button>
        </AddFlex>
      );
    }
    return (
      <AddFlex gap="10px">
        <Button
          variant="solid"
          onClick={() => {
            onClose(false);
          }}
          size="medium"
        >
          {"Cancel"}
        </Button>
        <Button
          variant="solid"
          // color={primaryActionColor}
          style={{
            backgroundColor: primaryActionColor,
            color: "white",
          }}
          onClick={() => {
            handleGoLive();
          }}
          size="medium"
        >
          {isApiLoading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : isCampagin ? (
            "Create"
          ) : (
            "Go Live"
          )}
        </Button>
      </AddFlex>
    );
  };

  if (showContacts) {
    return (
      <AddFlex
        position="fixed"
        top="0px"
        right="0px"
        height={windowSize.screenHeight + "px"}
        width="100%"
        backgroundColor="#F6F7F9"
        style={{
          boxShadow: "-10px 0px 40px 20px rgba(0,0,0,1)",
          overflow: "hidden",
        }}
      >
        <ResponsiveContactsList
          setShowContacts={setShowContacts}
          mediaId={automationData.mediaData.mediaId}
          showContactInformation={selectedAutomationData.otherAutomations.some(
            (automation) =>
              automation.type === automationTypes.REQUEST_EMAIL ||
              automation.type === automationTypes.REQUEST_MOBILE_NUMBER
          )}
          otherAutomations={selectedAutomationData.otherAutomations}
          automationId={selectedAutomationData.id}
        />
      </AddFlex>
    );
  }

  return (
    <>
      <AddFlex
        position="fixed"
        top="0px"
        left={isMobileView() ? "0px" : "18%"}
        height={windowSize.screenHeight + "px"}
        width={isMobileView() ? "100%" : "82%"}
        backgroundColor="#F6F7F9"
        style={{
          boxShadow: "-10px 0px 40px 20px rgba(0,0,0,1)",
          overflow: "hidden",
        }}
      >
        <AddFlex
          flexDirection="column"
          width={isMobileView() ? "100%" : "35%"}
          gap="20px"
          backgroundColor="white"
          style={{
            zIndex: 111111111111,
            borderLeft: isMobileView() ? "none" : "1px solid #ececec",
            boxShadow: "0px 0 5px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
            overflow: "hidden",
            flexShrink: 0,
            maxWidth: isMobileView() ? "unset" : "376px",
          }}
        >
          <div>
            {!isCampagin && isMobileView() && automationData.status && (
              <AddFlex
                alignItems="center"
                padding="10px"
                backgroundColor={
                  automationData.status === "live" ? "#249640" : "orange"
                }
                color="white"
                borderRadius="0 0px 12px 12px"
              >
                {automationData.status
                  ? "Automation Live!"
                  : "Automation Paused"}
                <Gap />
                <Button
                  onClick={() => {
                    setShowContacts(true);
                  }}
                >
                  View Contacts
                </Button>
              </AddFlex>
            )}
            <AddFlex
              alignItems="center"
              padding="15px"
              gap="10px"
              style={{ borderBottom: "1px solid #ececec" }}
            >
              <Close onClick={onClose} sx={{ cursor: "pointer" }} />
              <CustomText color="#242B39" fontSize="15px">
                {isMobileView()
                  ? "Comment to DM"
                  : "Auto-DM links from comments"}
              </CustomText>
              {!isMobileView() && automationData.status && (
                <AddFlex
                  padding="4px"
                  backgroundColor="#EC5038"
                  color="white"
                  borderRadius="4px"
                  style={{ fontSize: "15px" }}
                >
                  {capitalizeFirstLetter(automationData.status)}
                </AddFlex>
              )}
              <Gap />
              {isMobileView() && (
                <AddFlex gap="10px">{getTopBarButtons()}</AddFlex>
              )}
            </AddFlex>
          </div>
          <AddFlex
            flexDirection="column"
            padding={
              isMobileView() ? "0px 15px 15vh 15px" : "0px 15px 30px 15px"
            }
            gap="20px"
            style={{ overflowY: "scroll" }}
          >
            <div>
              <CustomText
                fontSize="20px"
                color="black"
                margin="0 0 5px 0"
                fontWeight="700"
              >
                Automation Name
              </CustomText>
              <Input
                disabled={!isNullOrUndefined(selectedAutomationData) && !isEdit}
                value={automationData.name}
                onChange={(e) => {
                  handleSetAutomationData("name", e.target.value);
                }}
                placeholder={"Give a name for this automation."}
              />
            </div>
            {stage >= 1 && (
              <UserInstaPosts
                automationData={automationData}
                handleSetAutomationData={handleSetAutomationData}
                isEdit={isEdit}
                disabled={!isNullOrUndefined(selectedAutomationData) && !isEdit}
                handleCheckCommentAutomationValidations={
                  handleCheckCommentAutomationValidations
                }
                isCampagin={isCampagin}
                setStage={setStage}
                errors={errors}
                stage={stage}
                setErrors={setErrors}
              />
            )}
            {stage >= 2 && (
              <>
                {!isCampagin && <div></div>}
                <InstaCommentToDMAutomationKeywords
                  automationData={automationData}
                  handleSetAutomationData={handleSetAutomationData}
                  isEdit={isEdit}
                  handleCheckCommentAutomationValidations={
                    handleCheckCommentAutomationValidations
                  }
                  isCampagin={isCampagin}
                  disabled={
                    !isNullOrUndefined(selectedAutomationData) && !isEdit
                  }
                  setStage={setStage}
                  stage={stage}
                  errors={errors}
                  setErrors={setErrors}
                />
              </>
            )}
            {stage >= 3 && (
              <>
                <div></div>
                <InstaCommentToDMAutomationMessage
                  automationData={automationData}
                  handleSetAutomationData={handleSetAutomationData}
                  isEdit={isEdit}
                  handleCheckCommentAutomationValidations={
                    handleCheckCommentAutomationValidations
                  }
                  disabled={
                    !isNullOrUndefined(selectedAutomationData) && !isEdit
                  }
                  isCampagin={isCampagin}
                  stage={stage}
                  setStage={setStage}
                  errors={errors}
                  setErrors={setErrors}
                />
              </>
            )}
            {stage >= 4 && (
              <>
                <div></div>
                <InstaCommentToDMAutomationOpeningMessage
                  automationData={automationData}
                  handleSetAutomationData={handleSetAutomationData}
                  isEdit={isEdit}
                  handleCheckCommentAutomationValidations={
                    handleCheckCommentAutomationValidations
                  }
                  isCampagin={isCampagin}
                  disabled={
                    !isNullOrUndefined(selectedAutomationData) && !isEdit
                  }
                  stage={stage}
                  setStage={setStage}
                  errors={errors}
                  setErrors={setErrors}
                />
              </>
            )}

            {stage >= 5 && (
              <>
                <div></div>
                <InstaCommentToDMOtherAutomations
                  automationData={automationData}
                  handleSetAutomationData={handleSetAutomationData}
                  isEdit={isEdit}
                  isOwnerInstagramIntegrated={isOwnerInstagramIntegrated}
                  handleCheckCommentAutomationValidations={
                    handleCheckCommentAutomationValidations
                  }
                  isCampagin={isCampagin}
                  disabled={
                    !isNullOrUndefined(selectedAutomationData) && !isEdit
                  }
                  stage={stage}
                  setStage={setStage}
                  errors={errors}
                  setErrors={setErrors}
                />
                <AutomationTags
                  disabled={
                    !isNullOrUndefined(selectedAutomationData) && !isEdit
                  }
                  isCampagin={isCampagin}
                  automationData={automationData}
                  handleSetAutomationData={handleSetAutomationData}
                />
              </>
            )}
          </AddFlex>
        </AddFlex>
        {!isMobileView() && (
          <AddFlex position="relative" grow={1}>
            <AddFlex
              gap="10px"
              padding="10px 30px"
              alignItems="center"
              style={{
                position: "absolute",
                top: "0px",
                right: "0%",
                width: "100%",
                zIndex: 1111111,
                backgroundColor: "#F6F7F9",
              }}
            >
              {!isCampagin && selectedAutomationData && (
                <>
                  <Tabs
                    selectedTabIndex={selectedTab}
                    setSelectedTabIndex={setSelectedTab}
                    tabs={[{ name: "Preview" }, { name: "Contacts" }]}
                  />
                </>
              )}
              <Gap />
              {getTopBarButtons()}
            </AddFlex>
            {selectedTab === 1 ? (
              <AddFlex width="100%">
                <ContactListTable
                  margin={"60px 30px"}
                  mediaId={selectedAutomationData?.mediaData?.mediaId}
                  automationStats={selectedAutomationData.stats}
                  // list={getTempList()}
                  showContactInformation={selectedAutomationData.otherAutomations.some(
                    (automation) =>
                      automation.type === automationTypes.REQUEST_EMAIL ||
                      automation.type === automationTypes.REQUEST_MOBILE_NUMBER
                  )}
                  isStory={selectedAutomationData?.mediaData?.isStory}
                  otherAutomations={selectedAutomationData.otherAutomations}
                  automationId={selectedAutomationData.id}
                  campaginId={selectedAutomationData.campaginId}
                  partial={true}
                />
              </AddFlex>
            ) : (
              <InstagramDMPreview automationData={automationData} />
            )}
          </AddFlex>
        )}
      </AddFlex>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <DialogHeader
          mainText={"ATTENTION!"}
          subText={"Please be careful, this operation cannot be undone"}
          onClose={closeDialog}
          padding={"10px 20px"}
        />
        <AddFlex flexDirection="column" gap="20px" padding="20px">
          <CustomText color="black">
            There is an automation for the post you selcted, do you want to
            replce it?
          </CustomText>
          <AddFlex gap="10px">
            <Gap />
            <SecondaryActionButton padding="5px 10px" onClick={closeDialog}>
              Cancel
            </SecondaryActionButton>
            <SecondaryActionButton
              padding="5px 10px"
              onClick={() => {
                handleGoLive(true);
                closeDialog();
              }}
            >
              Proceed
            </SecondaryActionButton>
          </AddFlex>
        </AddFlex>
      </Dialog>
    </>
  );
}

export default CreateUpdateInstaCommentAutomation;
